import React, { useMemo, useRef, useState } from 'react';
import { PaperClipIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { useCollaboratorsPublicQuery } from '@/features/Collaborators/Data';
import type { CategoriaAnexoAtivo, Equipamento } from '../../../../models';
import { useEquipamentoMutations, useHistoricoEquipamentoQuery } from '../../../../hooks/data';
import { formatarData } from './ativoHistorico.format';
import TermoDonoCampo, { DONO_TERMO_VAZIO, donoTermoValido, type DonoTermo } from './TermoDonoCampo';

export const CATEGORIA_ANEXO_LABELS: Record<CategoriaAnexoAtivo, string> = {
  nota_fiscal: 'Nota fiscal',
  foto: 'Foto',
  termo: 'Termo',
  outro: 'Outro',
};

/**
 * Aba "Anexos" da ficha: arquivos anexados à mão (NF, fotos, outros) + os termos gerados nas
 * Movimentações, que aparecem sozinhos a partir do histórico (entrega da Movimentação ou
 * histórico recuperado) — sem mexer no fluxo de gerar termo, que já funciona. Remover só
 * tira da lista: o arquivo fica guardado e a remoção vai pro histórico.
 */
export default function AtivoAnexos({ equipamento }: { equipamento: Equipamento }) {
  const { user } = useAuth();
  const { adicionarAnexo, removerAnexo } = useEquipamentoMutations();
  const { data: eventos } = useHistoricoEquipamentoQuery(equipamento.id);
  const [categoria, setCategoria] = useState<CategoriaAnexoAtivo>('nota_fiscal');
  const inputRef = useRef<HTMLInputElement>(null);
  const [donoTermo, setDonoTermo] = useState<DonoTermo>(DONO_TERMO_VAZIO);
  const { data: colaboradores = [] } = useCollaboratorsPublicQuery();
  const atual = colaboradores.find((c) => c.id === equipamento.colaboradorAtualId);
  const responsavelAtual = atual ? { id: atual.id, name: atual.name } : null;
  const ehTermo = categoria === 'termo';
  const podeAnexar = !ehTermo || donoTermoValido(donoTermo);

  const autor = user ? { id: user.collaboratorId ?? user.uid, name: user.displayName ?? user.email ?? '', avatar: null } : null;
  const anexos = [...(equipamento.anexos ?? [])].sort((a, b) => b.criadoEm.toMillis() - a.criadoEm.toMillis());

  const termos = useMemo(() => {
    const porUrl = new Map<string, { url: string; data: string; solicitacaoId: string | null }>();
    for (const evento of eventos) {
      const url = evento.origem?.termoUrl ?? evento.recuperado?.termoUrl;
      if (url && !porUrl.has(url)) {
        porUrl.set(url, { url, data: formatarData(evento.dataEvento ?? evento.em), solicitacaoId: evento.origem?.solicitacaoId ?? null });
      }
    }
    return Array.from(porUrl.values());
  }, [eventos]);

  const enviar = (arquivo: File | undefined) => {
    if (!arquivo || !autor) return;
    adicionarAnexo.mutate(
      { id: equipamento.id, arquivo, categoria, autor, dono: ehTermo ? donoTermo.colaborador : null },
      {
        onSettled: () => inputRef.current && (inputRef.current.value = ''),
        onSuccess: () => setDonoTermo(DONO_TERMO_VAZIO),
      }
    );
  };

  return (
    <div className="space-y-5">
      <section className="flex flex-wrap items-end gap-3 rounded-md border border-gray-200 p-3">
        <div>
          <label className="block text-sm font-medium text-gray-700">Tipo do arquivo</label>
          <select className="input-primary mt-1" value={categoria} onChange={(e) => setCategoria(e.target.value as CategoriaAnexoAtivo)}>
            {Object.entries(CATEGORIA_ANEXO_LABELS).map(([valor, label]) => (
              <option key={valor} value={valor}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <input ref={inputRef} type="file" className="hidden" onChange={(e) => enviar(e.target.files?.[0])} />
        <button
          type="button"
          className="btn-secondary flex items-center gap-1.5 text-sm"
          disabled={adicionarAnexo.isPending || !podeAnexar}
          title={podeAnexar ? undefined : 'Escolha de quem é o termo'}
          onClick={() => inputRef.current?.click()}
        >
          <PaperClipIcon className="h-4 w-4" />
          {adicionarAnexo.isPending ? 'Enviando...' : 'Anexar arquivo'}
        </button>
        {ehTermo && <TermoDonoCampo valor={donoTermo} onChange={setDonoTermo} responsavelAtual={responsavelAtual} />}
      </section>

      <section>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Arquivos</p>
        {anexos.length === 0 ? (
          <p className="mt-1 text-sm text-gray-400">Nenhum arquivo anexado.</p>
        ) : (
          <ul className="mt-2 divide-y divide-gray-100 rounded-md border border-gray-200">
            {anexos.map((anexo) => (
              <li key={anexo.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">{CATEGORIA_ANEXO_LABELS[anexo.categoria]}</span>
                <a href={anexo.url} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-blue-700 hover:underline">
                  {anexo.nome}
                </a>
                {anexo.categoria === 'termo' && (
                  <span className="text-xs text-gray-500">{anexo.colaboradorNome ? `de ${anexo.colaboradorNome}` : 'antigo · ninguém vê'}</span>
                )}
                <span className="text-xs text-gray-400">
                  {anexo.autor?.name} · {formatarData(anexo.criadoEm)}
                </span>
                <button
                  type="button"
                  className="text-gray-400 hover:text-red-600"
                  title="Remover da ficha (fica registrado no histórico)"
                  onClick={() => {
                    if (autor && window.confirm(`Remover "${anexo.nome}" da ficha?`)) {
                      removerAnexo.mutate({ id: equipamento.id, anexo, autor });
                    }
                  }}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Termos das Movimentações</p>
        {termos.length === 0 ? (
          <p className="mt-1 text-sm text-gray-400">
            Nenhum termo encontrado no histórico
            {!equipamento.historicoDesdeCriacao && !equipamento.historicoRecuperadoEm ? ' (busque o histórico anterior na aba Histórico)' : ''}.
          </p>
        ) : (
          <ul className="mt-2 divide-y divide-gray-100 rounded-md border border-gray-200">
            {termos.map((termo) => (
              <li key={termo.url} className="flex items-center gap-3 px-3 py-2 text-sm">
                <a href={termo.url} target="_blank" rel="noreferrer" className="flex-1 text-blue-700 hover:underline">
                  Termo · {termo.data}
                </a>
                {termo.solicitacaoId && (
                  <a href={`/si/solicitacoes/${termo.solicitacaoId}`} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">
                    ver Solicitação
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
